import { styled } from '@zeiss/beyond-online-react';

import { Element } from './types';

interface ContainerProps {
  $mode: BoxMode;
  $spacing: ResponsiveSpacing | Spacing;
}

const Container = styled.div<ContainerProps>`
  &:not(:last-child) {
    margin-bottom: ${({ $mode, $spacing }) =>
      /* istanbul ignore next -- @preserve */
      $mode === 'responsive'
        ? `var(--spacing-responsive-${$spacing})`
        : `var(--spacing-${$spacing})`};
  }
`;

type BoxMode = 'responsive' | 'static';

/**
 * Redeclaring these spacing types here due to a compile issue with typescript
 * that causes the type not be correctly inferred in the usage within `BoxProps`
 */
type ResponsiveSpacing =
  '2xl' | '2xs' | '3xl' | 'l' | 'm' | 'modular' | 's' | 'xl' | 'xs';

type Spacing =
  | '2xl'
  | '2xs'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | '8xl'
  | '9xl'
  | 'l'
  | 'm'
  | 'nano'
  | 'px'
  | 's'
  | 'xl'
  | 'xs';

export interface BoxProps<T extends BoxMode = 'responsive'> {
  /**
   * Set the HTML element to render as.
   *
   * - `div`: Use to wrap generic content.
   * - `section`: Use to wrap content that belongs together and which meets the
   *    requirements of the generic HTML section element. It should almost
   *    always include a headline.
   *
   * @defaultValue div
   */
  as?: Element;
  /**
   * Content.
   */
  children?: React.ReactNode;
  /**
   * Spacing mode to use: 'responsive' or 'static'
   *
   * @defaultValue 'responsive'
   */
  mode?: T;
  /**
   * Set spacing to the next element depending on `mode`
   *
   * @defaultValue m
   */
  spacing?: T extends 'responsive' ? ResponsiveSpacing : Spacing;
}

/**
 * Primitive layout component to wrap compositions/components together and to
 * control their spacing to the next element.
 *
 * @see {@link https://designsystem.zeiss.com/document/8#/grid-layout/spacings}
 */
export const Box = <T extends BoxMode = 'responsive'>({
  as = 'div',
  children,
  mode,
  spacing,
  ...rest
}: BoxProps<T>) => (
  <Container
    $mode={mode || 'responsive'}
    $spacing={spacing || 'm'}
    as={as}
    {...rest}
  >
    {children}
  </Container>
);
